import axios from 'axios';
import { BACKEND_URL } from '../config_and_helpers/config';
import { logError, logInfo, logWarn } from '../config_and_helpers/logger';
import {
  buildQuoteExtractionSnapshot,
  buildRawQuoteApiSnapshot,
  logRheniumQuoteExtractionDebug,
  resolveQuoteExtractionItems,
  resolveQuoteExtractionSource,
  RHENIUM_QUOTE_FORMAT_ID,
} from '../utils/quoteExtraction/logQuoteExtraction';
import {
  emptyToNull,
  normalizeSupplierNameAndWebsite,
  pickNonEmptyString,
  pickNumber,
} from '../utils/quoteExtraction/normalizeQuoteExtractionFields';
import { extractQuoteFromFile } from '../utils/quoteExtraction/extractQuoteFromFile';

const LOG_CONTEXT = 'quote_extraction_client';

const MAX_PDF_BYTES = 10 * 1024 * 1024;

/**
 * Coerce API extraction payload into a stable review shape.
 *
 * @param {Object} raw - Response body or nested `data`
 * @param {string} [sourceFileName]
 * @returns {import('../types/quoteExtractionTypes').ExtractedQuoteData}
 */
/**
 * @param {Object} delivery
 * @returns {string}
 */
function formatDeliveryAddressString(delivery) {
  if (!delivery || typeof delivery !== 'object') return '';
  const parts = [
    delivery.recipient,
    delivery.location,
    delivery.buildingOrStreet ?? delivery.building_or_street,
    delivery.city,
    delivery.postalCode ?? delivery.postal_code,
  ]
    .map(p => (p == null ? '' : String(p).trim()))
    .filter(Boolean);
  return parts.join(', ');
}

export function normalizeExtractedQuoteResponse(raw, sourceFileName = '') {
  const { source, wrappers } = resolveQuoteExtractionSource(raw || {});
  const { items: rawItems, sourceKey: itemsSourceKey } = resolveQuoteExtractionItems(source);

  const supplier = source.supplier && typeof source.supplier === 'object' ? source.supplier : {};
  const supplierContact =
    source.supplierContact && typeof source.supplierContact === 'object'
      ? source.supplierContact
      : source.supplier_contact && typeof source.supplier_contact === 'object'
        ? source.supplier_contact
        : {};
  const customer = source.customer && typeof source.customer === 'object' ? source.customer : {};
  const deliveryRaw =
    source.deliveryAddress && typeof source.deliveryAddress === 'object'
      ? source.deliveryAddress
      : source.delivery_address && typeof source.delivery_address === 'object'
        ? source.delivery_address
        : null;
  const quote = source.quote && typeof source.quote === 'object' ? source.quote : {};
  const totals =
    source.totals && typeof source.totals === 'object' ? source.totals : {};
  const items = rawItems;
  const warnings = Array.isArray(source.warnings) ? source.warnings : [];
  const meta = source.meta && typeof source.meta === 'object' ? { ...source.meta } : {};

  if (!meta.sourceFileName && sourceFileName) {
    meta.sourceFileName = sourceFileName;
  }
  if (
    !meta.supplierFormat &&
    (meta.parserFormat || meta.parser_format || meta.supplier_format)
  ) {
    meta.supplierFormat =
      meta.parserFormat || meta.parser_format || meta.supplier_format;
  }

  const companyId = emptyToNull(supplier.companyId ?? supplier.company_id);
  const taxId = emptyToNull(supplier.taxId ?? supplier.tax_id);
  const supplierNameWeb = normalizeSupplierNameAndWebsite(
    supplier.name,
    supplier.website
  );

  const deliveryAddress = deliveryRaw
    ? {
        recipient: emptyToNull(deliveryRaw.recipient),
        location: emptyToNull(deliveryRaw.location),
        buildingOrStreet: emptyToNull(
          deliveryRaw.buildingOrStreet ?? deliveryRaw.building_or_street
        ),
        city: emptyToNull(deliveryRaw.city),
        postalCode: emptyToNull(deliveryRaw.postalCode ?? deliveryRaw.postal_code),
      }
    : null;

  const deliveryStr = formatDeliveryAddressString(deliveryAddress);
  const customerDelivery =
    (customer.deliveryAddress ?? customer.delivery_address ?? deliveryStr) || null;

  const quoteHeader = {
    quoteNumber: pickNonEmptyString(quote.quoteNumber, quote.quote_number),
    quoteDate: pickNonEmptyString(quote.quoteDate, quote.quote_date),
    validUntil: pickNonEmptyString(quote.validUntil, quote.valid_until),
    customerOrderNumber: pickNonEmptyString(
      quote.customerOrderNumber,
      quote.customer_order_number
    ),
    paymentTerms: pickNonEmptyString(quote.paymentTerms, quote.payment_terms),
    note: pickNonEmptyString(quote.note),
    agentName: pickNonEmptyString(
      quote.agentName,
      quote.agent_name,
      supplierContact.name
    ),
    agentPhone: pickNonEmptyString(
      quote.agentPhone,
      quote.agent_phone,
      supplierContact.phone
    ),
    agentEmail: pickNonEmptyString(
      quote.agentEmail,
      quote.agent_email,
      supplierContact.email
    ),
    currency: pickNonEmptyString(
      quote.currency,
      totals.currency,
      totals.currency_code
    ),
    subtotalBeforeDiscount: pickNumber(
      quote.subtotalBeforeDiscount,
      quote.subtotal_before_discount
    ),
    subtotal: pickNumber(
      quote.subtotal,
      quote.subtotal_before_vat,
      totals.subtotalBeforeVat,
      totals.subtotal_before_vat
    ),
    generalDiscount: pickNumber(quote.generalDiscount, quote.general_discount),
    totalAfterDiscount: pickNumber(
      quote.totalAfterDiscount,
      quote.total_after_discount
    ),
    vatRate: pickNumber(quote.vatRate, quote.vat_rate, totals.vatRate, totals.vat_rate),
    vatAmount: pickNumber(
      quote.vatAmount,
      quote.vat_amount,
      totals.vatAmount,
      totals.vat_amount
    ),
    total: pickNumber(quote.total, totals.total),
  };

  const result = {
    supplier: {
      name: supplierNameWeb.name,
      hebrewName: pickNonEmptyString(supplier.hebrewName, supplier.hebrew_name),
      companyId,
      website: supplierNameWeb.website,
      email: pickNonEmptyString(supplier.email),
      phone: pickNonEmptyString(supplier.phone),
      fax: pickNonEmptyString(supplier.fax),
      taxId,
      address: pickNonEmptyString(supplier.address),
    },
    supplierContact: {
      name: pickNonEmptyString(supplierContact.name),
      phone: pickNonEmptyString(supplierContact.phone),
      email: pickNonEmptyString(supplierContact.email),
    },
    customer: {
      name: pickNonEmptyString(customer.name),
      departmentOrLab: pickNonEmptyString(
        customer.departmentOrLab,
        customer.department_or_lab
      ),
      companyId: pickNonEmptyString(customer.companyId, customer.company_id),
      customerNumber: pickNonEmptyString(
        customer.customerNumber,
        customer.customer_number
      ),
      phone: pickNonEmptyString(customer.phone),
      email: pickNonEmptyString(customer.email),
      contactPerson: pickNonEmptyString(
        customer.contactPerson,
        customer.contact_person
      ),
      deliveryAddress: emptyToNull(customerDelivery),
      billingContact: pickNonEmptyString(
        customer.billingContact,
        customer.billing_contact
      ),
      billingAddress: pickNonEmptyString(
        customer.billingAddress,
        customer.billing_address
      ),
    },
    deliveryAddress,
    quote: quoteHeader,
    totals: {
      subtotalBeforeVat: pickNumber(
        totals.subtotalBeforeVat,
        totals.subtotal_before_vat
      ),
      vatRate: pickNumber(totals.vatRate, totals.vat_rate),
      vatAmount: pickNumber(totals.vatAmount, totals.vat_amount),
      total: pickNumber(totals.total),
      currency: pickNonEmptyString(totals.currency),
    },
    items: items.map((row, index) => {
      const productNumber = pickNonEmptyString(
        row.productNumber,
        row.product_number
      );
      const catalogNumber = pickNonEmptyString(
        row.catalogNumber,
        row.catalog_number,
        productNumber
      );
      return {
        lineNumber: pickNumber(row.lineNumber, row.line_number) ?? index + 1,
        catalogNumber,
        productNumber,
        description: pickNonEmptyString(row.description),
        casNumber: pickNonEmptyString(row.casNumber, row.cas_number),
        eta: pickNonEmptyString(row.eta),
        quantity: pickNumber(row.quantity),
        unit: pickNonEmptyString(row.unit),
        unitPriceBeforeDiscount: pickNumber(
          row.unitPriceBeforeDiscount,
          row.unit_price_before_discount
        ),
        discountPercent: pickNumber(row.discountPercent, row.discount_percent),
        unitPriceAfterDiscount: pickNumber(
          row.unitPriceAfterDiscount,
          row.unit_price_after_discount
        ),
        unitPrice: pickNumber(row.unitPrice, row.unit_price),
        unitPriceCurrency: pickNonEmptyString(
          row.unitPriceCurrency,
          row.unit_price_currency
        ),
        lineTotal: pickNumber(row.lineTotal, row.line_total),
        lineTotalCurrency: pickNonEmptyString(
          row.lineTotalCurrency,
          row.line_total_currency
        ),
        productDataSheetUrl: pickNonEmptyString(
          row.productDataSheetUrl,
          row.product_data_sheet_url
        ),
        rawLine: pickNonEmptyString(row.rawLine, row.raw_line),
        rawUrlLine: pickNonEmptyString(row.rawUrlLine, row.raw_url_line),
      };
    }),
    warnings: warnings.map(w => ({
      field: w.field,
      message: String(w.message || ''),
      severity: w.severity === 'error' || w.severity === 'info' ? w.severity : 'warning',
    })),
    meta,
  };

  const formatId = String(meta.supplierFormat || meta.parserFormat || '').toLowerCase();
  const isRheniumFormat =
    formatId === RHENIUM_QUOTE_FORMAT_ID || formatId === 'rhenium_quote_pdf';
  const isEncoFormat = formatId === 'enco_scientific_quote_pdf';

  if (isRheniumFormat) {
    result.supplier = {
      name: null,
      hebrewName: null,
      companyId: null,
      website: null,
      email: null,
      phone: null,
      fax: null,
      taxId: null,
      address: null,
    };
    result.customer = {
      name: null,
      departmentOrLab: null,
      companyId: null,
      customerNumber: null,
      phone: null,
      email: null,
      contactPerson: null,
      deliveryAddress: null,
      billingContact: null,
      billingAddress: null,
    };
    result.deliveryAddress = null;
    result.quote = {
      ...result.quote,
      quoteDate: null,
      validUntil: null,
      paymentTerms: null,
      agentName: null,
      agentPhone: null,
      agentEmail: null,
      vatRate: null,
      vatAmount: null,
      subtotalBeforeDiscount: null,
      subtotal: null,
      generalDiscount: null,
      totalAfterDiscount: null,
      total: null,
    };
    result.totals = {
      ...result.totals,
      vatRate: null,
      vatAmount: null,
      subtotalBeforeDiscount: null,
      subtotal: null,
      generalDiscount: null,
      totalAfterDiscount: null,
      total: null,
    };
  }

  if (
    isRheniumFormat ||
    isEncoFormat ||
    (itemsSourceKey && itemsSourceKey !== 'items')
  ) {
    const audit = {
      wrappers,
      itemsSourceKey: itemsSourceKey || 'items',
      itemCount: result.items.length,
    };
    if (itemsSourceKey && itemsSourceKey !== 'items') {
      logWarn(LOG_CONTEXT, 'normalize used alternate items array key', audit);
    }
    logRheniumQuoteExtractionDebug('after_normalize', {
      fileName: sourceFileName || meta.sourceFileName || null,
      rawBody: raw,
      normalized: result,
      extra: audit,
    });
  }

  if (isEncoFormat) {
    logInfo(LOG_CONTEXT, 'normalizeExtractedQuoteResponse ENCO result', {
      fileName: sourceFileName || meta.sourceFileName || null,
      parserSource: meta.parserSource ?? null,
      supplierName: result.supplier?.name ?? null,
      quoteNumber: result.quote?.quoteNumber ?? null,
      itemCount: result.items.length,
      normalized: buildQuoteExtractionSnapshot(result),
    });
  }

  return result;
}

/**
 * User-safe message from extraction API error body.
 *
 * @param {*} errorData
 * @param {string} fallback
 * @returns {string}
 */
function extractionErrorMessage(errorData, fallback) {
  if (!errorData) return fallback;
  if (typeof errorData === 'string') return errorData;
  if (errorData.error && typeof errorData.error === 'string') return errorData.error;
  if (errorData.detail && typeof errorData.detail === 'string') return errorData.detail;
  if (errorData.message && typeof errorData.message === 'string') return errorData.message;
  if (Array.isArray(errorData.non_field_errors) && errorData.non_field_errors[0]) {
    return String(errorData.non_field_errors[0]);
  }
  return fallback;
}

/**
 * POST supplier quote PDF to Django extract endpoint (server-side).
 *
 * @param {string} token
 * @param {File} file
 * @returns {Promise<import('../types/quoteExtractionTypes').ExtractedQuoteResponse & { message?: string }>}
 */
async function extractQuotePdfFromServer(token, file) {
  const formData = new FormData();
  formData.append('quote', file);

  const response = await axios.post(`${BACKEND_URL}quotes/extract/`, formData, {
    headers: {
      Authorization: `Token ${token}`,
      'Content-Type': 'multipart/form-data',
    },
    timeout: 300000,
    maxContentLength: Infinity,
    maxBodyLength: Infinity,
  });

  const body = response.data || {};

  logRheniumQuoteExtractionDebug('api_raw', {
    fileName: file.name,
    rawBody: body,
    extra: { rawSnapshot: buildRawQuoteApiSnapshot(body) },
  });

  if (body.success === false) {
    const message = extractionErrorMessage(
      body,
      'Could not extract quote data from this PDF.'
    );
    logError(LOG_CONTEXT, 'extractQuotePdfFromServer rejected', {
      fileName: file.name,
      message,
    });
    return { success: false, error: message, message };
  }

  const data = normalizeExtractedQuoteResponse(body, file.name);

  logInfo(LOG_CONTEXT, 'extractQuotePdfFromServer success', {
    fileName: file.name,
    supplierFormat: data.meta?.supplierFormat ?? null,
    itemCount: data.items.length,
    parserSource: data.meta?.parserSource ?? 'django_server',
  });

  logRheniumQuoteExtractionDebug('extract_success', {
    fileName: file.name,
    rawBody: body,
    normalized: data,
  });

  return { success: true, data };
}

export async function extractQuotePdf(token, file) {
  if (!file || file.type !== 'application/pdf') {
    return {
      success: false,
      error: 'Invalid file type',
      message: 'Only PDF files are supported.',
    };
  }
  if (file.size > MAX_PDF_BYTES) {
    return {
      success: false,
      error: 'File too large',
      message: 'File size must be less than 10MB.',
    };
  }

  logInfo(LOG_CONTEXT, 'extractQuotePdf start', {
    fileName: file.name,
    fileSize: file.size,
  });

  try {
    const clientResult = await extractQuoteFromFile(file);

    if (clientResult.success && clientResult.data) {
      const data = normalizeExtractedQuoteResponse(
        { success: true, data: clientResult.data },
        file.name
      );

      logInfo(LOG_CONTEXT, 'extractQuotePdf client success', {
        fileName: file.name,
        supplierFormat: data.meta?.supplierFormat ?? null,
        parserSource: data.meta?.parserSource ?? null,
        itemCount: data.items.length,
        warningCount: data.warnings?.length ?? 0,
      });

      logRheniumQuoteExtractionDebug('extract_success', {
        fileName: file.name,
        normalized: data,
        extra: { parserSource: 'react_client_pdfjs' },
      });

      return { success: true, data };
    }

    logWarn(LOG_CONTEXT, 'client extract skipped or failed — trying server', {
      fileName: file.name,
      message: clientResult.message || clientResult.error,
    });

    if (!token) {
      return {
        success: false,
        error: clientResult.error || 'Authentication required for server extraction',
        message:
          clientResult.message ||
          'Client extraction failed and no auth token for server fallback.',
      };
    }

    return await extractQuotePdfFromServer(token, file);
  } catch (error) {
    const status = error.response?.status;
    const errorData = error.response?.data;
    const message = extractionErrorMessage(
      errorData,
      'Could not extract quote data from this PDF.'
    );

    logError(LOG_CONTEXT, 'extractQuotePdf failed', {
      fileName: file.name,
      status: status ?? null,
      message,
    });

    if (errorData && typeof errorData === 'object' && errorData.data) {
      return {
        success: false,
        error: message,
        message,
        data: normalizeExtractedQuoteResponse(errorData, file.name),
      };
    }

    return {
      success: false,
      error: message,
      message,
    };
  }
}
